// `defineRoute` + `createRegistry` + `flattenRegistry` — authoring helpers.

import type {
  CallerKind,
  FlatEntry,
  HttpVerb,
  RegistryNode,
  RegistryTree,
  RouteEntry,
  VerbSpec,
} from './types';

/**
 * Identity helper that pins the `params` generic from the literal you pass, so
 * the path-builder and client know the exact param names. Brands the result so
 * the registry walkers can distinguish a route leaf from a nested group.
 *
 * @example
 *   const detail = defineRoute({
 *     path: '/api/auctions/:id',
 *     params: { id: '' },
 *     methods: { GET: {}, PATCH: {}, DELETE: {} },
 *   });
 */
export function defineRoute<
  P extends Record<string, string>,
  M extends Partial<Record<HttpVerb, VerbSpec>>,
>(entry: { path: string; params: P; methods: M; caller?: CallerKind }): RouteEntry<P, M> {
  return { ...entry, __routeEntry: true };
}

/** Type guard: is this node a route leaf (vs. a nested subtree)? */
export function isRouteEntry(node: RegistryNode): node is RouteEntry<Record<string, string>> {
  return (node as RouteEntry).__routeEntry === true;
}

/**
 * Validate a registry tree at module load and return it frozen. Throws on:
 *   - duplicate dotted keys (can't happen via object literal, but guards codegen),
 *   - the same `path` + verb declared by two different entries (a real route can
 *     only be one file; two registry entries for it is a drift bug).
 *
 * @returns the same tree, deeply frozen, typed as `T` for client inference.
 */
export function createRegistry<T extends RegistryTree>(tree: T): T {
  const flat = flattenRegistry(tree);

  const seenKeys = new Set<string>();
  const seenPaths = new Map<string, string>(); // path -> key
  for (const entry of flat) {
    if (seenKeys.has(entry.key)) {
      throw new Error(`[api-registry] duplicate registry key: '${entry.key}'`);
    }
    seenKeys.add(entry.key);

    // One route folder = one entry. Enforcing unique paths (not just path+verb)
    // keeps the parity harness's path→entry map lossless: two entries sharing a
    // path with disjoint verb sets would otherwise pass here and let one silently
    // shadow the other during the verb diff.
    const priorPath = seenPaths.get(entry.path);
    if (priorPath) {
      throw new Error(
        `[api-registry] duplicate route path: '${entry.path}' declared by both ` +
          `'${priorPath}' and '${entry.key}'. A route folder maps to exactly one entry — ` +
          `merge their verbs into one defineRoute.`,
      );
    }
    seenPaths.set(entry.path, entry.key);
  }

  return deepFreeze(tree);
}

/**
 * Walk a registry tree depth-first, yielding one {@link FlatEntry} per route
 * leaf with its derived dotted key (the nesting path). Used by the parity test
 * and the dead-route report.
 */
export function flattenRegistry(tree: RegistryTree): FlatEntry[] {
  const out: FlatEntry[] = [];
  const walk = (node: RegistryTree, prefix: string[]): void => {
    for (const [name, child] of Object.entries(node)) {
      const path = [...prefix, name];
      if (isRouteEntry(child)) {
        out.push({
          key: path.join('.'),
          path: child.path,
          methods: Object.keys(child.methods) as HttpVerb[],
          caller: child.caller ?? 'internal',
        });
      } else {
        walk(child, path);
      }
    }
  };
  walk(tree, []);
  return out;
}

function deepFreeze<T>(obj: T): T {
  if (obj && typeof obj === 'object') {
    for (const value of Object.values(obj)) deepFreeze(value);
    Object.freeze(obj);
  }
  return obj;
}
